import { useState, useEffect, useCallback, useRef } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { obtenerMemoria } from "./useMemoriaAgente";

function fechaHoyStr() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, "0");
  const dd   = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function planRef(uid) {
  return doc(db, "miDiaPlan", `${uid}_${fechaHoyStr()}`);
}
function completadasRef(uid) {
  return doc(db, "miDiaCompletadas", `${uid}_${fechaHoyStr()}`);
}
function urgentesKey(uid) {
  return `midia_urgentes_${uid}`;
}

// Convierte cualquier fecha a "YYYY-MM-DD" usando hora LOCAL (no UTC)
function serializarFecha(val) {
  if (!val) return null;
  let d;
  if (val instanceof Date)  d = val;
  else if (val?.toDate)     d = val.toDate();
  else return val;
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function serializarLista(lista) {
  return lista.map(c => ({
    ...c,
    proximoSeguimiento: serializarFecha(c.proximoSeguimiento),
    creadoEn:           null,
    actualizadoEn:      null,
    estadoCambiadoEn:   null,
  }));
}

export function useMiDia(clientes, cotizaciones, perfil, userId) {
  const [tareas,           setTareas]           = useState([]);
  const [resumen,          setResumen]          = useState("");
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState(null);
  const [completadas,      setCompletadas]      = useState([]);
  const [notasCompletadas, setNotasCompletadas] = useState({});
  const [omitidas,         setOmitidas]         = useState([]); // reprogramadas (ocultas hoy, no completadas)

  const fetchedRef = useRef(false);

  // Cargar completadas + omitidas desde Firestore al iniciar
  useEffect(() => {
    if (!userId) return;
    getDoc(completadasRef(userId))
      .then(snap => {
        if (snap.exists()) {
          const data = snap.data();
          setCompletadas(data.ids     || []);
          setNotasCompletadas(data.notas   || {});
          setOmitidas(data.omitidas || []);
        } else {
          // Migración desde localStorage (código anterior)
          const oldKey = `midia_done_${userId}_${fechaHoyStr()}`;
          try {
            const old = JSON.parse(localStorage.getItem(oldKey) || "[]");
            if (old.length > 0) {
              setCompletadas(old);
              setDoc(completadasRef(userId), { ids: old, notas: {}, omitidas: [], userId, fecha: fechaHoyStr() }).catch(() => {});
            }
          } catch {}
        }
      })
      .catch(() => {});
  }, [userId]);

  const cargarTareas = useCallback(async (forzar = false) => {
    if (!userId || !perfil || clientes.length === 0) return;

    // Caché válido por 3 horas — después regenera automáticamente para
    // reflejar cambios hechos por otros asesores (seguimientos actualizados)
    const MAX_EDAD_MS = 3 * 60 * 60 * 1000;

    if (!forzar) {
      try {
        const snap = await getDoc(planRef(userId));
        if (snap.exists()) {
          const data = snap.data();
          const edad = Date.now() - (data.timestamp || 0);
          if (edad < MAX_EDAD_MS) {
            setTareas(data.tareas  || []);
            setResumen(data.resumen || "");
            const urgentes = (data.tareas || []).filter(t => t.prioridad === "urgente").length;
            localStorage.setItem(urgentesKey(userId), String(urgentes));
            return;
          }
          // Plan vencido — cae al API call para regenerar con datos frescos
        }
      } catch {}
    }

    setLoading(true);
    setError(null);

    try {
      const memoria = await obtenerMemoria(userId);

      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch("/api/midia", {
        method:  "POST",
        headers: {
          "Content-Type":  "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          clientes:      serializarLista(clientes),
          cotizaciones:  serializarLista(cotizaciones),
          memoria,
          nombreUsuario: perfil.nombre || "Asesor",
          rol:           perfil.rol    || "asesor",
          fechaHoy:      fechaHoyStr(),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Error ${res.status}`);
      }

      const data    = await res.json();
      const payload = { ...data, timestamp: Date.now(), userId };

      await setDoc(planRef(userId), payload).catch(() => {});

      // Al forzar, limpiar completadas + omitidas para que el nuevo plan empiece limpio
      if (forzar) {
        const limpio = { ids: [], notas: {}, omitidas: [], userId, fecha: fechaHoyStr() };
        await setDoc(completadasRef(userId), limpio).catch(() => {});
        setCompletadas([]);
        setNotasCompletadas({});
        setOmitidas([]);
      }

      const urgentes = (data.tareas || []).filter(t => t.prioridad === "urgente").length;
      localStorage.setItem(urgentesKey(userId), String(urgentes));

      setTareas(data.tareas  || []);
      setResumen(data.resumen || "");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clientes, cotizaciones, perfil, userId]);

  // Carga automática una sola vez cuando los datos estén listos
  useEffect(() => {
    if (!fetchedRef.current && clientes.length > 0 && userId && perfil) {
      fetchedRef.current = true;
      cargarTareas();
    }
  }, [clientes.length, userId, cargarTareas]);

  const marcarHecho = useCallback((taskId, nota = "") => {
    if (!userId) return;
    setCompletadas(prev => {
      if (prev.includes(taskId)) return prev;
      const nuevasIds = [...prev, taskId];
      setNotasCompletadas(prevNotas => {
        const nuevasNotas = nota ? { ...prevNotas, [taskId]: nota } : prevNotas;
        setDoc(completadasRef(userId), {
          ids:      nuevasIds,
          notas:    nuevasNotas,
          omitidas, // preservar omitidas al escribir
          userId,
          fecha:    fechaHoyStr(),
        }).catch(() => {});
        return nuevasNotas;
      });
      return nuevasIds;
    });
  }, [userId, omitidas]);

  // Marcar como omitida (reprogramada): se oculta hoy sin contar como completada
  const marcarOmitida = useCallback((taskId) => {
    if (!userId) return;
    setOmitidas(prev => {
      if (prev.includes(taskId)) return prev;
      const nuevas = [...prev, taskId];
      setDoc(completadasRef(userId), { omitidas: nuevas }, { merge: true }).catch(() => {});
      return nuevas;
    });
  }, [userId]);

  // Excluir tanto completadas como omitidas de la vista pendiente
  const tareasPendientes = tareas.filter(t => !completadas.includes(t.id) && !omitidas.includes(t.id));

  return {
    tareas:           tareasPendientes,
    todasLasTareas:   tareas,
    totalTareas:      tareas.length,
    resumen,
    loading,
    error,
    completadas,
    notasCompletadas,
    omitidas,
    marcarHecho,
    marcarOmitida,
    recargar: () => { fetchedRef.current = false; cargarTareas(true); },
  };
}
